import { isArray, isEmpty } from 'lodash-es';
import { NzTableFilterValue } from 'ng-zorro-antd/table';

export type FilterValueType = Date | boolean | number | string | null;

export type ODataFilter =
  IFilter | IFilterGroup | Record<string, FilterValueType | FilterValueType[]> | string[];

export interface IFilter {
  key: string;
  value?: FilterValueType | FilterValueType[];
  operator: ODataOperator;
}

export interface IFilterGroup {
  filters: Array<IFilter | IFilterGroup | string>;
  andOr: ODataAndOr;
}

export enum ODataOperator {
  Equals = 'eq',
  NotEquals = 'ne',
  GreaterThan = 'gt',
  GreaterThanOrEquals = 'ge',
  LessThan = 'lt',
  LessThanOrEquals = 'le',
  Contains = '$contains({key}, {value})',
  SubstringOf = '$substringof({value}, {key})',
}

export enum ODataAndOr {
  And = 'and',
  Or = 'or',
}

export type ODataVersion = '2' | '3' | '4';

const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

const includeQuotes = (value: FilterValueType): boolean => {
  if (typeof value === 'string') {
    // GUIDs are OData v4 literals and must NOT be quoted.
    return !uuidRegex.test(value);
  }

  // eslint-disable-next-line no-null/no-null
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return false;
  }

  console.warn(`${typeof value} is not a known type by the OData param mapper`);

  return true;
};

const convertValueToString = (value: FilterValueType): string => {
  // eslint-disable-next-line no-null/no-null
  if (value === null) {
    return 'null';
  }

  if (value instanceof Date) {
    return `${value.toISOString()}`;
  }

  if (includeQuotes(value)) {
    // OData escapes a literal quote by doubling it. Without this, a search for
    // "O'Brien" emits '*O'BRIEN*' and the service rejects the malformed clause.
    return `'${(value as string).replace(/'/gu, "''")}'`;
  }

  return `${value}`;
};

const createArrayString = (array: FilterValueType[]): string =>
  array.map((value) => convertValueToString(value)).join(',');

const getSupportedOperator = (
  operator: ODataOperator,
  oDataVersion: ODataVersion
): ODataOperator => {
  if (operator === ODataOperator.Contains && oDataVersion === '2') {
    return ODataOperator.SubstringOf;
  }

  return operator;
};

const handleFilterValue = (
  key: string,
  value: FilterValueType | FilterValueType[] | undefined,
  operator: ODataOperator,
  oDataVersion: ODataVersion
): string => {
  if (!value) {
    return '';
  }

  if (isArray(value)) {
    if (value.length === 0) {
      return '';
    }

    if (oDataVersion === '4') {
      return `${key} in (${createArrayString(value)})`;
    }

    const filterComponents = value.map((x) => {
      const stringValue = convertValueToString(x);

      return `${key} eq ${stringValue}`;
    });

    return `(${filterComponents.join(' or ')})`;
  }

  const stringValue = convertValueToString(value);
  const supportedOperator = getSupportedOperator(operator, oDataVersion);

  if (supportedOperator.startsWith('$')) {
    const oDataFunction = supportedOperator.substring(1);

    return oDataFunction.replace('{value}', stringValue).replace('{key}', key);
  }

  return `${key} ${supportedOperator} ${stringValue}`;
};

const createFilter = (
  key: string,
  value: FilterValueType | FilterValueType[] | undefined,
  operator: ODataOperator,
  oDataVersion: ODataVersion
): string => {
  const keys = key.split('||');
  const keyFilters = keys
    .map((splitKey) => handleFilterValue(splitKey, value, operator, oDataVersion))
    .filter((element) => !!element);

  if (keyFilters.length === 0) {
    return '';
  }

  if (keyFilters.length === 1) {
    return keyFilters[0];
  }

  return `(${keyFilters.join(' or ')})`;
};

const isIFilter = (filter: ODataFilter): filter is IFilter => !!(filter as IFilter).operator;

const isIFilterGroup = (filter: ODataFilter): filter is IFilterGroup =>
  !!(filter as IFilterGroup).andOr;

const handleIFilter = (filter: IFilter, oDataVersion: ODataVersion): string =>
  createFilter(filter.key, filter.value, filter.operator, oDataVersion);

const handleIFilterGroup = (filterGroup: IFilterGroup, oDataVersion: ODataVersion): string => {
  if (!filterGroup.filters.length) {
    return '';
  }

  const filterStrings = filterGroup.filters.map((filter) => {
    if (typeof filter === 'string') {
      return filter;
    }

    if (isIFilter(filter)) {
      return handleIFilter(filter, oDataVersion);
    }

    return handleIFilterGroup(filter, oDataVersion);
  });

  const filterString = filterStrings.filter((x) => x).join(` ${filterGroup.andOr} `);

  if (!filterString) {
    return '';
  }

  return `(${filterString})`;
};

export const mapODataFilters = (
  filters?: ODataFilter,
  oDataVersion: ODataVersion = '4'
): string | undefined => {
  if (!filters) {
    return undefined;
  }

  if (isIFilter(filters)) {
    return handleIFilter(filters, oDataVersion);
  }

  if (isIFilterGroup(filters)) {
    return handleIFilterGroup(filters, oDataVersion);
  }

  const filterComponents = Array.isArray(filters)
    ? filters
    : Object.entries(filters).map(([key, value]) =>
        createFilter(key, value, ODataOperator.Equals, oDataVersion)
      );

  const definedFilterComponents = filterComponents.filter((x) => !!x);

  if (isEmpty(definedFilterComponents)) {
    return undefined;
  }

  return `(${definedFilterComponents.join(') and (')})`;
};

export const convertFiltersToFilterGroup = (filters: ODataFilter): IFilterGroup => {
  if (isIFilterGroup(filters)) {
    return filters;
  }

  if (Array.isArray(filters)) {
    return {
      andOr: ODataAndOr.And,
      filters,
    };
  }

  if (isIFilter(filters)) {
    return {
      andOr: ODataAndOr.And,
      filters: [filters],
    };
  }

  return {
    andOr: ODataAndOr.And,
    filters: Object.entries(filters).reduce((prev: IFilter[], [key, value]) => {
      prev.push({
        key,
        value,
        operator: ODataOperator.Equals,
      });

      return prev;
    }, []),
  };
};

export const convertNzFiltersToFilterGroup = (
  filters: Array<{
    key: string;
    value: NzTableFilterValue;
  }>
): IFilterGroup => {
  const record: Record<string, FilterValueType | FilterValueType[]> = {};

  filters.forEach((filter) => {
    record[filter.key] = filter.value as FilterValueType | FilterValueType[];
  });

  return convertFiltersToFilterGroup(record);
};
